import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { key: 0 }

import { computed } from 'vue';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

import ProductColor from './ProductColor.vue';
import ProductVariant from './ProductVariant.vue';
import CustomSelect from '../CustomSelector.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductVariantSelector',
  props: {
  selectedQuantity: {
    type: Number,
    default: 1,
  },
  productData: {
    type: Object,
  },
  selectedVariant: {
        type: Object,
  },
},
  emits: ['setSelectedVariant'],
  setup(__props, { emit }) {

const props = __props;

const { websiteText } = useWebsiteTexts();





const currentVariantId = computed(() => {
  return props.selectedVariant?.id || props.selectedVariant?.articleNumber;
});

const dimensions = computed(() => {
  return props.productData?.fieldsJson?.Dimensions || [];
});

const productVariants = computed(() => {
  return props.productData?.variants || [];
});

const dimensionValues = computed(() => {
  return dimensions.value.reduce((acc: any, dim: any) => {
    acc[dim.value] = new Set(
      productVariants.value
        .map((v: any) => getVariantField(v, dim.value))
        .filter((f: any) => f)
    );
    return acc;
  }, {});
});

const selectedVariantDimensions = computed(() => {
  const res = dimensions.value.reduce((result: any, curr: any) => {
    const variantField = getVariantField(props.selectedVariant, curr.value);

    if (variantField || typeof variantField === 'boolean') {
      result.push(variantField);
    }
    return result;
  }, []);

  return res;
});

function resolvePrice(v: any) {
    const price = v.prices || v.price;
    if (!price) return 0;
    
    return price?.campaignPrice ? price.campaignPrice : (price.originalPrice || 0)
}

const currentVariants = computed(() => {
  const variantsLeft = getSelectedVariants(selectedVariantDimensions.value);
  
  return (
    variantsLeft?.map((v: any) => ({
      name: v.fields.VariantLabel || v.fields._name,
      id: v.id || v.articleNumber,
      price: resolvePrice(v),
    })) || []
  );
});

const variantDropdownContent = computed(() => {
  return currentVariants.value.length > 1 ? currentVariants.value : undefined;
});

const currentDropdownVariant = computed(() => {
  return currentVariants.value.find(
    (v: any) => v.id === currentVariantId.value
  );
});

function getVariantField(variant: any, field: any) {
  const fieldValue =
    variant?.fields?.[field] ?? variant?.technicalSpecifications?.[field];
  return fieldValue?.value?.toString() || fieldValue?.toString();
}

function getVariantFieldName(variant: any, field: any) {
  const fieldValue =
    variant?.fields?.[field] ?? variant?.technicalSpecifications?.[field];
  return fieldValue?.name || fieldValue?.toString() || fieldValue;
}

function getDimensionValues(dimension: any): Array<any> {
  return Array.from(dimensionValues.value[dimension]);
}

function areAnyVariantsAvailableForDimensionValue(
  dimension: any,
  dimensionValue: any,
  dimensionIndex: any,
  selection: any
) {
  return (
    productVariants.value.filter((v: any) => {
      for (let i = 0; i < dimensionIndex; i++) {
        if (getVariantField(v, dimensions.value?.[i]?.value) !== selection[i]) {
          return false;
        }
      }

      return getVariantField(v, dimension) === dimensionValue;
    })?.length > 0
  );
}

function getAvailableFields(field: any, index: any) {
  const fieldValues = getDimensionValues(field)?.filter((d: any) => {
    return areAnyVariantsAvailableForDimensionValue(
      field,
      d,
      index,
      selectedVariantDimensions.value
    );
  });

  const fieldNames = Array.from(
    new Set(
      productVariants.value
        .map((p: any) => getVariantFieldName(p, field))
        .filter((f: any, idx: number) => {
          return fieldValues.includes(
            getVariantField(productVariants.value[idx], field)
          )
            ? f
            : false;
        })
    )
  );

  return fieldValues.map((v, idx) => {
    return {
      name: fieldNames[idx],
      value: v,
    };
  });
}

function selectVariantDimension(dimensionIndex: any, dimensionValue: any) {
  const selection = [...selectedVariantDimensions.value];

  selection.splice(
    dimensionIndex,
    selectedVariantDimensions.value.length,
    dimensionValue
  );

  // preselect next layers
  for (let i = dimensionIndex + 1; i < dimensions.value.length; i++) {
    for (const dimValue of getDimensionValues(dimensions.value?.[i]?.value)) {
      if (
        areAnyVariantsAvailableForDimensionValue(
          dimensions.value?.[i]?.value,
          dimValue,
          i,
          selection
        )
      ) {
        selection.push(dimValue);
        break;
      }
    }
  }

  const variant = getSelectedVariants(selection)?.[0];
  emit('setSelectedVariant', variant);
}

function getSelectedVariants(selection: any) {
  return dimensions.value.length === 0
    ? productVariants.value
    : productVariants.value.filter((v: any) => {
        for (let i = 0; i < dimensions.value.length; i++) {
          if (
            getVariantField(v, dimensions.value?.[i]?.value) !== selection[i]
          ) {
            return false;
          }
        }
        return true;
      });
}

function selectVariantFromDropdown(id: any) {
  const variant: any = productVariants.value.find(
    (v: any) => (v.id || v.articleNumber) === id
  );
  emit('setSelectedVariant', variant);
}

return (_ctx: any,_cache: any) => {
  return (_unref(dimensions).length || _unref(variantDropdownContent))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(dimensions), (field, index) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (field.value === 'Color')
              ? (_openBlock(), _createBlock(ProductColor, {
                  key: field.value,
                  "color-label": field.name,
                  colors: getAvailableFields(field.value, index),
                  selectedColor: _unref(selectedVariantDimensions)[index],
                  onNewSelected: ($event: any) => (selectVariantDimension(index, $event))
                }, null, 8, ["color-label", "colors", "selectedColor", "onNewSelected"]))
              : _createCommentVNode("", true),
            (field.value !== 'Color')
              ? (_openBlock(), _createBlock(ProductVariant, {
                  key: field.value,
                  "variant-label": field.name,
                  variants: getAvailableFields(field.value, index),
                  selectedVariant: _unref(selectedVariantDimensions)[index],
                  onNewSelected: ($event: any) => (selectVariantDimension(index, $event))
                }, null, 8, ["variant-label", "variants", "selectedVariant", "onNewSelected"]))
              : _createCommentVNode("", true)
          ], 64))
        }), 256)),
        (_unref(variantDropdownContent))
          ? (_openBlock(), _createBlock(CustomSelect, {
              key: 0,
              class: "product-details__custom-select",
              options: _unref(variantDropdownContent),
              placeholder: 
        _unref(websiteText)('product__select_variant').value ||
        'product__select_variant'
      ,
              selectedOption: _unref(currentDropdownVariant),
              onNewSelected: selectVariantFromDropdown
            }, null, 8, ["options", "placeholder", "selectedOption"]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})